import { measureShapeParagraphsPlainTextLength } from '@/lib/text-linking/measure-paragraphs-plain-text-length'
import type { ChainCaretFrame } from './types/chain-caret-frame'
import type { ChainCaretDestination } from './types/chain-caret-destination'
import type { ChainFrameSubRange } from './types/chain-frame-sub-range'

/**
 * Walks `frames` (chain-flow order, starting at the first frame the hand-off content lands in)
 * accumulating each frame's plain-text length until `offset` — measured from the start of
 * `frames[0]`'s content — falls within one of them. Used by `handleShapeChainOverflowCaret`
 * (use-home-chain-editing.ts) to translate a caret position that fell inside newly handed-off content into the
 * (possibly multi-hop cascaded) destination frame + local offset — a single overflowing edit (a
 * large paste, or a font-size bump that shrinks capacity across several linked frames at once) can
 * push content past more than one frame boundary in one reflow.
 *
 * Every length here is measured via `measureShapeParagraphsPlainTextLength`
 * (`paragraphsToPlainText` — paragraphs joined by `\n`, a paragraph's own `textLines`/`textRuns`
 * concatenated with no separator), the same convention `enterChainFrameEditAtOffset`'s shape-side
 * caret math uses — every chain member is shape-backed now (WMPCM-7988/9070), so this is the only
 * live convention.
 *
 * Falls back to the end of the last frame when `offset` exceeds the combined length of all
 * frames (should not normally happen — the chain's last frame absorbs all remaining content
 * unclipped — but keeps this pure function total rather than throwing on unexpected input).
 * Returns null only when `frames` is empty.
 */
export function locateChainCaretDestination(
  frames: ChainCaretFrame[],
  offset: number
): ChainCaretDestination | null {
  if (frames.length === 0) return null

  let remaining = offset

  for (const frame of frames) {
    const length = measureShapeParagraphsPlainTextLength(frame.paragraphs)

    if (remaining <= length) {
      return { frameId: frame.id, offset: remaining }
    }

    remaining -= length
  }

  const last = frames[frames.length - 1]!

  return { frameId: last.id, offset: measureShapeParagraphsPlainTextLength(last.paragraphs) }
}

/**
 * Maps a frame-local raw-text offset to a chain-global offset — the inverse of the frame-walk in
 * `locateChainCaretDestination`: the sum of every preceding frame's length plus `localOffset`.
 * `frameIndex` is clamped into `[0, frameLengths.length]`.
 */
export function frameLocalToGlobalOffset(
  frameLengths: number[],
  frameIndex: number,
  localOffset: number
): number {
  const clampedIndex = Math.max(0, Math.min(frameIndex, frameLengths.length))
  let base = 0

  for (let i = 0; i < clampedIndex; i++) {
    base += frameLengths[i]!
  }

  return base + localOffset
}

/**
 * A chain-global offset resolved back to the frame that owns it — the `frameLengths` counterpart of
 * `locateChainCaretDestination`, and the inverse of `frameLocalToGlobalOffset`.
 *
 * At an exact frame boundary the **earlier** frame wins (`localOffset` equals that frame's length),
 * matching `locateChainCaretDestination`'s `remaining <= length` test: the caret belongs after the
 * last character of the frame the user was in, not before the first character of the next one.
 * Clamps to the end of the last frame when `global` exceeds the total. `frameIndex` is 0 for an
 * empty `frameLengths`.
 */
export function globalToFrameLocalOffset(
  frameLengths: number[],
  global: number
): { frameIndex: number; localOffset: number } {
  if (frameLengths.length === 0) return { frameIndex: 0, localOffset: 0 }

  let remaining = Math.max(0, global)

  for (let i = 0; i < frameLengths.length; i++) {
    const length = frameLengths[i]!

    if (remaining <= length) return { frameIndex: i, localOffset: remaining }

    remaining -= length
  }

  const last = frameLengths.length - 1

  return { frameIndex: last, localOffset: frameLengths[last]! }
}

/**
 * Slices a chain selection (`anchorGlobalOffset`…`focusGlobalOffset`, given in either order) into
 * per-frame local ranges by intersecting the normalized `[lo, hi]` span with each frame's global
 * span. Emits an entry for every covered frame — including fully-covered middle frames — and skips
 * frames the selection does not touch (empty intersection). Single source of truth for painting
 * the cross-frame highlight.
 */
export function computeChainSelectionSubRanges(
  frameLengths: number[],
  anchorGlobalOffset: number,
  focusGlobalOffset: number
): ChainFrameSubRange[] {
  const lo = Math.min(anchorGlobalOffset, focusGlobalOffset)
  const hi = Math.max(anchorGlobalOffset, focusGlobalOffset)
  const result: ChainFrameSubRange[] = []
  let frameStart = 0

  for (let i = 0; i < frameLengths.length; i++) {
    const frameEnd = frameStart + frameLengths[i]!
    const start = Math.max(lo, frameStart)
    const end = Math.min(hi, frameEnd)

    if (end > start) {
      result.push({ frameIndex: i, start: start - frameStart, end: end - frameStart })
    }

    frameStart = frameEnd
  }

  return result
}
